import { DateTime } from "luxon";
import { Base64 } from "js-base64";
import { UserConsent, UserData } from "../model/User";
import MiddleFunction from "../mixins/MiddleFunction";

import { tokenService } from "../services/tokenService";

import i18n from "../providers/i18n";

interface Partner {
  name: string;
  userToken: string;
  userData: UserData | undefined;
  userConsent: UserConsent;
  hospitalId: number | undefined;
  appointmentCode: string | undefined;
  createdAt: string;
}

const LOCALSTORAGE_KEY = "SvhPartner";

// Helper function to clear localStorage and sessionStorage while preserving specific items
const clearStoragesExceptSpecific = () => {
  console.log("Clearing localStorage and sessionStorage except specific items");

  // 1. Save values to preserve
  const lastRegisteredTime = localStorage.getItem("lastRegisteredTime");
  const lastRegisteredUserData = localStorage.getItem("lastRegisteredUserData");
  const lang = localStorage.getItem("lang");

  // 2. Clear storages
  localStorage.clear();
  sessionStorage.clear();

  // 3. Restore preserved values
  if (lastRegisteredTime) {
    localStorage.setItem("lastRegisteredTime", lastRegisteredTime);
  }

  if (lastRegisteredUserData) {
    localStorage.setItem("lastRegisteredUserData", lastRegisteredUserData);
  }

  if (lang) {
    localStorage.setItem("lang", lang);
  }

  console.log("Storage clearing completed, preserved items restored");
};

const partnerService = {
  // Initialize partner.
  // Get partner from query string.
  // Set partner object in local storage.
  // If partner is not found, clear local storage.
  initPartner: async (location: Location) => {
    // Short-circuit: reuse stored partner when the token hasn't changed and
    // userData is already decoded. This prevents redundant /Token/Decrypt and
    // /Token/VerifyAccessToken calls caused by multiple initPartner callers
    // (App.tsx + Agreement.tsx) and React StrictMode double-invoking effects.
    const tokenQueryString = new URLSearchParams(location.search).get("token");
    const storedPartner = partnerService.getPartner();
    if (
      storedPartner &&
      tokenQueryString &&
      storedPartner.userToken === tokenQueryString &&
      storedPartner.userData
    ) {
      return storedPartner;
    }

    // First try to get partner and user data from query string.
    const partnerQueryString = new URLSearchParams(location.search).get(
      "partner"
    ) as string;
    const utmSourceQueryString = new URLSearchParams(location.search).get(
      "utm_source"
    ) as string;
    const typeQueryString = new URLSearchParams(location.search).get(
      "type"
    ) as string;
    const appointmentCodeQueryString = new URLSearchParams(location.search).get(
      "code"
    ) as string;

    // Trim any spaces from utmSourceQueryString to handle cases like "LINE " (with trailing space)
    const cleanedUtmSource = utmSourceQueryString
      ? utmSourceQueryString.trim()
      : utmSourceQueryString;

    // Determine partner name from query parameters
    // Extract partner and language from type parameter (e.g., "THAILIFE-EN" -> partner: "THAILIFE", lang: "EN")
    let typePartner: string | undefined;
    let typeLang: string | undefined;
    if (typeQueryString) {
      const typeParts = typeQueryString.split("-");
      typePartner = typeParts[0];
      typeLang = typeParts[1];
    }
    const partnerName = partnerQueryString || cleanedUtmSource || typePartner || "Webview";

    // Set language if extracted from type parameter, validating against supported locales
    if (typeLang) {
      const normalizedLang = typeLang.toLowerCase() as "en" | "th";
      const SUPPORTED_LANGS: ReadonlyArray<string> = ["en", "th"];
      if (SUPPORTED_LANGS.includes(normalizedLang)) {
        // changeLanguage updates the running i18n instance immediately and
        // the languageChanged listener in providers/i18n.ts persists it to localStorage.
        i18n.changeLanguage(normalizedLang);
      }
    }

    // Check if partner is "Alive" (case-insensitive)
    const isAlivePartner = partnerName.toLowerCase().trim() === "alive";

    // Clear storages for non-Alive partners
    if (!isAlivePartner) {
      console.log(
        `Initializing non-Alive partner: ${partnerName}, clearing storages`
      );
      clearStoragesExceptSpecific();
    } else {
      console.log(`Initializing Alive partner, skipping storage clearing`);
    }

    const partner: Partner = {
      name: partnerName,
      userToken: tokenQueryString ?? "",
      userData: undefined,
      userConsent: {
        isAcceptTerms: false,
        isConsent3rdParty: false,
        isConsentMarketing: false,
      },
      hospitalId: undefined,
      appointmentCode: undefined,
      createdAt: DateTime.local().toISO() as string,
    };

    if (partner.userToken) {
      partner.userData = await tokenService.decryptUserDataToken(
        partner.userToken,
        partner.name,
        location
      );
    }

    if (appointmentCodeQueryString) {
      partner.appointmentCode = appointmentCodeQueryString;
    }

    if (!partner.userData) {
      partner.userData = partnerService.initPartnerUserData(location);
    }

    if (partner.userData) {
      partner.userData = partnerService.processPartnerUserData(
        partner.userData,
        partner.name
      );
    }

    // If user data is not found, try reading from local storage.
    if (!partner.userData) {
      const partnerLocalStorage = partnerService.getPartner();
      if (partnerLocalStorage && partnerLocalStorage.name === partner.name) {
        partner.userToken = partnerLocalStorage.userToken;
        partner.userData = partnerLocalStorage.userData;
      }
    }

    partnerService.setPartner(partner);

    return partner;
  },

  // Set partner object in local storage.
  // Encode partner object as base64 and store in local storage.
  setPartner: (partner: Partner) => {
    const partnerString = JSON.stringify(partner);
    const partnerBase64 = Base64.encode(partnerString);
    localStorage.setItem(LOCALSTORAGE_KEY, partnerBase64);
  },

  // Get partner object from local storage.
  // Decode partner object from base64.
  getPartner: () => {
    const partnerBase64 = localStorage.getItem(LOCALSTORAGE_KEY);
    if (partnerBase64) {
      const partnerString = Base64.decode(partnerBase64);
      const partner = JSON.parse(partnerString) as Partner;
      // If partner is not expired, return it.
      const createdAt = DateTime.fromISO(partner.createdAt);
      const now = DateTime.local();
      const diff = now.diff(createdAt, "hours");
      if (diff.hours < 2) {
        return partner;
      } else {
        return undefined;
      }
    }
    return undefined;
  },

  // Initialize partner user data from query string.
  initPartnerUserData: (location: Location) => {
    // Parse user data passed in query string.
    if (
      new URLSearchParams(location.search).get("firstName") ||
      new URLSearchParams(location.search).get("firstname") ||
      new URLSearchParams(location.search).get("lastName") ||
      new URLSearchParams(location.search).get("lastname") ||
      new URLSearchParams(location.search).get("birthDate") ||
      new URLSearchParams(location.search).get("birthdate") ||
      new URLSearchParams(location.search).get("birthDay") ||
      new URLSearchParams(location.search).get("birthday") ||
      new URLSearchParams(location.search).get("tel") ||
      new URLSearchParams(location.search).get("telephone") ||
      new URLSearchParams(location.search).get("idCard") ||
      new URLSearchParams(location.search).get("idcard") ||
      new URLSearchParams(location.search).get("cardId") ||
      new URLSearchParams(location.search).get("cardid") ||
      new URLSearchParams(location.search).get("email")
    ) {
      const userData: UserData = {
        firstName: (new URLSearchParams(location.search).get("firstName") ||
          new URLSearchParams(location.search).get("firstname")) as string,
        lastName: (new URLSearchParams(location.search).get("lastName") ||
          new URLSearchParams(location.search).get("lastname")) as string,
        birthdate:
          ((new URLSearchParams(location.search).get("birthDate") ||
            new URLSearchParams(location.search).get("birthdate")) as string) ||
          ((new URLSearchParams(location.search).get("birthDay") ||
            new URLSearchParams(location.search).get("birthday")) as string),
        tel: (new URLSearchParams(location.search).get("tel") ||
          new URLSearchParams(location.search).get("telephone")) as string,
        idCard:
          ((new URLSearchParams(location.search).get("idCard") ||
            new URLSearchParams(location.search).get("idcard")) as string) ||
          ((new URLSearchParams(location.search).get("cardId") ||
            new URLSearchParams(location.search).get("cardid")) as string),
        email: new URLSearchParams(location.search).get("email") as string,
        gender: (new URLSearchParams(location.search).get("gender") ||
          "") as string,
        hn: (new URLSearchParams(location.search).get("hn") || "") as string,
        accessToken: (new URLSearchParams(location.search).get("accessToken") ||
          "") as string,
        idToken: (new URLSearchParams(location.search).get("idToken") ||
          "") as string,
        searchOption: "",
      };
      return userData;
    } else {
      return undefined;
    }
  },

  // Process partner user data.
  processPartnerUserData: (userData: UserData, partnerName: string) => {
    // Process birthdate.
    if (userData.birthdate !== "" && !userData.birthdate.includes("-")) {
      let dateParts = userData.birthdate.split("/");
      if (dateParts.length === 1) {
        dateParts = userData.birthdate.split("-");
      }
      userData.birthdate =
        dateParts[2] + "-" + dateParts[1] + "-" + dateParts[0];
    }

    // Process gender.
    if (userData.gender !== "") {
      if (
        userData.gender.toLowerCase() === "male" ||
        userData.gender.toLowerCase() === "m"
      ) {
        userData.gender = "ชาย";
      } else if (
        userData.gender.toLowerCase() === "female" ||
        userData.gender.toLowerCase() === "f"
      ) {
        userData.gender = "หญิง";
      }
    }

    // Process hn.
    // Trim any spaces from partnerName to handle cases like "LINE " (with trailing space)
    const cleanedPartnerName = partnerName.toLowerCase().trim();
    switch (cleanedPartnerName) {
      case "thailife":
        userData.hn = `TL-${userData.idCard}`;
        break;
      case "sansiri":
        userData.hn = `SANSIRI-${userData.idCard}`;
        break;
      case "line":
        userData.hn = `LN-${userData.tel}`;
        break;
      case "Allianz":
        userData.hn = `Allianz-${MiddleFunction.methods._randomstring("tel")}`;
        break;
      default:
        userData.hn = userData.hn
          ? userData.hn
          : `Webview-${MiddleFunction.methods._randomstring("tel")}`;
        break;
    }

    return userData;
  },
};

export { partnerService, Partner };
